<template>
  <v-dialog
    v-model="internalValue"
    max-width="750px"
    scrollable
    transition="dialog-right-transition"
  >
    <v-card class="d-flex flex-column rounded-lg">
      <v-card-title class="pa-4 bg-slate-50 border-bottom d-flex align-center justify-space-between">
        <div class="d-flex align-center">
          <v-avatar color="indigo lighten-5" size="36" class="mr-3">
            <v-icon color="indigo darken-2">mdi-history</v-icon>
          </v-avatar>
          <div>
            <div class="text-subtitle-1 font-weight-bold grey--text text--darken-4">
              Trazabilidad de Asignaciones de Volumen
            </div>
            <div class="caption grey--text text--darken-1">
              Historial de deducciones y movimientos de la bolsa sin cliente
            </div>
          </div>
        </div>
        <v-btn icon small @click="internalValue = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="pa-4 flex-grow-1 overflow-y-auto">
        <div v-if="loading" class="text-center py-6">
          <v-progress-circular indeterminate color="indigo" size="32"></v-progress-circular>
          <div class="caption grey--text text--darken-1 mt-2">Cargando trazabilidad...</div>
        </div>

        <div v-else-if="!historyItems || historyItems.length === 0" class="text-center py-8">
          <v-icon size="48" color="grey lighten-1">mdi-clipboard-text-off-outline</v-icon>
          <div class="text-subtitle-2 grey--text text--darken-2 mt-2">No hay asignaciones registradas</div>
          <div class="caption grey--text">Aún no se ha transferido volumen de la bolsa sin cliente a ningún cliente comercial.</div>
        </div>

        <div v-else>
          <v-timeline dense align-top>
            <v-timeline-item
              v-for="(item, idx) in historyItems"
              :key="idx"
              :color="getStatusColor(item.EnaStatus)"
              small
            >
              <v-card class="border-light rounded-lg pa-3 mb-2">
                <div class="d-flex flex-wrap align-center justify-space-between mb-1">
                  <div class="font-weight-bold grey--text text--darken-4 text-subtitle-2">
                    {{ item.CustomerName || item.EnaCustomerName || 'Cliente SAP no especificado' }}
                  </div>
                  <v-chip
                    x-small
                    label
                    :color="getStatusColor(item.EnaStatus)"
                    class="white--text font-weight-bold"
                  >
                    {{ item.StatusName || getStatusText(item.EnaStatus) }}
                  </v-chip>
                </div>

                <!-- Info Grid -->
                <v-row dense class="mt-1">
                  <v-col cols="12" sm="4">
                    <span class="caption grey--text text--darken-1">TM Asignadas: </span>
                    <span class="font-weight-bold grey--text text--darken-4">{{ formatNumber(item.EnaAllocatedTons, 2) }} TM</span>
                  </v-col>
                  <v-col cols="12" sm="4">
                    <span class="caption grey--text text--darken-1">FCL Prop.: </span>
                    <span class="font-weight-bold grey--text text--darken-3">{{ formatNumber(item.EnaProportionalFcl, 2) }}</span>
                  </v-col>
                  <v-col cols="12" sm="4">
                    <span class="caption grey--text text--darken-1">FOB Prop.: </span>
                    <span class="font-weight-bold green--text text--darken-3">${{ formatNumber(item.EnaProportionalFobUsd, 2) }}</span>
                  </v-col>
                </v-row>

                <!-- Motivo u observaciones -->
                <div v-if="item.EnaReason || item.EnaObservation" class="caption grey--text text--darken-2 mt-2 bg-slate-50 pa-2 rounded">
                  <div v-if="item.EnaReason">
                    <strong>Motivo:</strong> {{ item.EnaReason }}
                  </div>
                  <div v-if="item.EnaObservation">
                    <strong>Obs:</strong> {{ item.EnaObservation }}
                  </div>
                </div>

                <!-- Metadata de auditoría -->
                <div class="caption grey--text text--darken-1 mt-2 d-flex flex-wrap justify-space-between align-center border-top-dashed pt-1">
                  <span>
                    <v-icon x-small color="grey">mdi-account</v-icon>
                    {{ item.CreatedByName || ('Usuario #' + item.SecUserCreatedID) }}
                  </span>
                  <span>
                    <v-icon x-small color="grey">mdi-clock-outline</v-icon>
                    {{ formatDate(item.SecDateCreated) }}
                  </span>
                </div>

                <!-- Botón de reversión -->
                <div v-if="!isView && item.EnaStatus === 1" class="text-right mt-2">
                  <v-btn
                    x-small
                    text
                    color="red darken-2"
                    class="font-weight-bold text-capitalize"
                    @click="$emit('revert-allocation', item)"
                  >
                    <v-icon x-small left>mdi-undo</v-icon>
                    Revertir a bolsa
                  </v-btn>
                </div>
              </v-card>
            </v-timeline-item>
          </v-timeline>
        </div>
      </v-card-text>

      <v-card-actions class="pa-3 bg-slate-50 border-top justify-end">
        <v-btn text color="grey darken-2" @click="internalValue = false">
          Cerrar
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { formatNumber } from "../utils/CommercialProgrammingCalculations";
import moment from "moment";

export default {
  name: "CommercialUnassignedAllocationHistoryDrawer",
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    historyItems: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    isView: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    internalValue: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
  },
  methods: {
    formatNumber,
    formatDate(val) {
      if (!val) return "-";
      return moment(val).format("DD/MM/YYYY HH:mm");
    },
    getStatusColor(status) {
      switch (status) {
        case 1:
          return "green darken-2";
        case 2:
          return "grey darken-1";
        case 3:
          return "blue darken-2";
        default:
          return "grey";
      }
    },
    getStatusText(status) {
      switch (status) {
        case 1:
          return "Activo";
        case 2:
          return "Revertido";
        case 3:
          return "Reasignado";
        default:
          return "Desconocido";
      }
    },
  },
};
</script>

<style scoped>
.bg-slate-50 {
  background-color: #f8fafc !important;
}
.border-bottom {
  border-bottom: 1px solid #e2e8f0 !important;
}
.border-top {
  border-top: 1px solid #e2e8f0 !important;
}
.border-light {
  border: 1px solid #e2e8f0 !important;
}
.border-top-dashed {
  border-top: 1px dashed #cbd5e1 !important;
}
</style>
